<template>
    <form name="form-sponsor" id="form-sponsor" class="sponsor-form" novalidate @submit="onSubmit" @input="markDirty" @change="markDirty">
        <input type="hidden" name="id" v-model="model.id">
        <input type="hidden" name="pic" v-model="model.image">

        <p class="form-required-note"><span aria-hidden="true">*</span> Required fields</p>

        <div v-if="Object.keys(fieldErrors).length" class="form-alert" role="alert" aria-live="assertive">
            <strong>Review the highlighted fields.</strong>
            <span>{{ formErrorMessage }}</span>
        </div>

        <div class="form-row sponsor-form-sections">
            <section class="col-lg-6 mb-4 mb-lg-0" aria-labelledby="sponsor-details-heading">
                <div class="sponsor-form-section">
                    <div class="section-heading">
                        <div>
                            <p class="section-eyebrow">SPONSORSHIP</p>
                            <h2 id="sponsor-details-heading">Sponsor details</h2>
                        </div>
                        <i class="pi pi-star" aria-hidden="true"></i>
                    </div>

                    <div class="form-group">
                        <label for="name">Name <span class="required-indicator" aria-hidden="true">*</span></label>
                        <InputText id="name" v-model="model.name" class="form-control" :class="{ 'p-invalid': fieldErrors.name }" aria-describedby="name-help name-error" required />
                        <small id="name-help" class="form-help">Use the sponsor name as it should appear to attendees.</small>
                        <small v-if="fieldErrors.name" id="name-error" class="field-error">{{ fieldErrors.name }}</small>
                    </div>

                    <div class="form-group">
                        <label for="category">Category <span class="required-indicator" aria-hidden="true">*</span></label>
                        <select id="category" class="form-control" v-model="model.category" :class="{ 'p-invalid': fieldErrors.category }" aria-describedby="category-help category-error" required>
                            <option :value="0" disabled>Select a category</option>
                            <option v-for="data in categories" :key="data.value" :value="data.value">{{ data.label }}</option>
                        </select>
                        <small id="category-help" class="form-help">Choose the sponsorship package assigned to this partner.</small>
                        <small v-if="fieldErrors.category" id="category-error" class="field-error">{{ fieldErrors.category }}</small>
                    </div>

                    <div class="form-group">
                        <label for="website">Website <span class="required-indicator" aria-hidden="true">*</span></label>
                        <InputText id="website" v-model="model.url" type="url" class="form-control" :class="{ 'p-invalid': fieldErrors.url }" aria-describedby="website-help website-error" required />
                        <small id="website-help" class="form-help">Enter a full URL, for example https://example.com.</small>
                        <small v-if="fieldErrors.url" id="website-error" class="field-error">{{ fieldErrors.url }}</small>
                    </div>

                    <div class="form-group mb-0">
                        <label for="status">Status <span class="required-indicator" aria-hidden="true">*</span></label>
                        <select id="status" class="form-control" v-model="model.status" :class="{ 'p-invalid': fieldErrors.status }" aria-describedby="status-help status-error" required>
                            <option :value="1">Active</option>
                            <option :value="0">Inactive</option>
                        </select>
                        <small id="status-help" class="form-help">Only active sponsors are shown in attendee-facing experiences.</small>
                        <small v-if="fieldErrors.status" id="status-error" class="field-error">{{ fieldErrors.status }}</small>
                    </div>
                </div>
            </section>

            <section class="col-lg-6" aria-labelledby="sponsor-content-heading">
                <div class="sponsor-form-section">
                    <div class="section-heading">
                        <div>
                            <p class="section-eyebrow">PRESENTATION</p>
                            <h2 id="sponsor-content-heading">Banner and description</h2>
                        </div>
                        <i class="pi pi-image" aria-hidden="true"></i>
                    </div>

                    <div class="form-group">
                        <label for="description">Description <span class="required-indicator" aria-hidden="true">*</span></label>
                        <Textarea id="description" v-model="model.description" class="form-control" :class="{ 'p-invalid': fieldErrors.description }" aria-describedby="description-help description-error" rows="7" required />
                        <small id="description-help" class="form-help">Describe the sponsor, its offering, and any information relevant to attendees.</small>
                        <small v-if="fieldErrors.description" id="description-error" class="field-error">{{ fieldErrors.description }}</small>
                    </div>

                    <div class="form-group mb-0">
                        <label for="sponsor-banner">Banner <span class="required-indicator" aria-hidden="true">*</span></label>
                        <small class="form-help upload-help">Upload a JPG, PNG, or WEBP image up to 5 MB. A wide landscape image works best.</small>
                        <FileUpload
                            id="sponsor-banner"
                            mode="advanced"
                            name="file"
                            :multiple="false"
                            accept="image/jpeg,image/png,image/webp"
                            :maxFileSize="5242880"
                            chooseLabel="Choose banner"
                            uploadLabel="Upload banner"
                            cancelLabel="Remove"
                            v-model="model.uploadFile"
                            :url="uploadUrl"
                            @select="markDirty"
                            @error="onUploadError"
                            @upload="onUploadSuccess"
                        />
                        <small v-if="fieldErrors.image" class="field-error">{{ fieldErrors.image }}</small>
                        <div v-if="imagePreviewUrl" class="banner-preview">
                            <img :src="imagePreviewUrl" alt="Current sponsor banner">
                            <span>Current banner</span>
                        </div>
                    </div>
                </div>
            </section>
        </div>

        <div class="form-actions">
            <p class="form-actions-note">{{ hasUnsavedChanges ? 'You have unsaved changes.' : 'All changes are saved when you submit.' }}</p>
            <div class="form-actions-buttons">
                <button type="button" class="btn btn-outline-secondary mr-sm-1" @click="onCancel">Cancel</button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                    <span v-if="saving" class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    {{ saving ? 'Saving…' : (isNew ? 'Create sponsor' : 'Save changes') }}
                </button>
            </div>
        </div>
    </form>

    <ConfirmDialog></ConfirmDialog>
</template>

<script>
import {defineComponent} from 'vue'
import apiService from "@/services/ApiService";
import FileUpload from "primevue/fileupload";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";

export default defineComponent({
    name: "SponsorForm",
    components: {
        FileUpload, InputText, Textarea
    },
    props: {
        id: {
            type: String,
            required: false
        },
        isNew: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            model: {
                id: '',
                name: '',
                category: 0,
                image: '',
                url: '',
                status: 1,
                description: '',
                uploadFile: ''
            },
            saving: false,
            hasUnsavedChanges: false,
            fieldErrors: {},
            categories: [],
            uploadUrl: ''
        }
    },
    computed: {
        formErrorMessage() {
            return Object.values(this.fieldErrors)[0];
        },
        imagePreviewUrl() {
            return this.model.image ? `${apiService.defaults.baseURL}/file/${this.model.image}` : '';
        }
    },
    methods: {
        onSubmit(event) {
            event.preventDefault();
            if (!this.validateForm()) {
                return;
            }

            this.saving = true;
            let url = '/sponsor/edit';
            let successMessage = 'Sponsor has been updated';
            let errorMessage = 'Failed to update sponsor';
            if(this.isNew){
                url = '/sponsor/add';
                successMessage = 'Sponsor has been added';
                errorMessage = 'Failed to add sponsor';
            }

            const payload = {
                ...this.model,
                category: Number(this.model.category),
                status: Number(this.model.status)
            };

            apiService.post(url, payload)
                .then(() => {
                    this.hasUnsavedChanges = false;
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: successMessage,
                        life: 5000
                    });
                })
                .catch(error => {
                    this.$toast.add({
                        severity: 'error',
                        summary: errorMessage,
                        detail: error.response?.data?.Message || 'Please try again.',
                        life: 5000
                    });
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        onCancel() {
            if (!this.hasUnsavedChanges) {
                this.$router.push('/sponsor');
                return;
            }

            this.$confirm.require({
                message: 'Discard your unsaved sponsor changes?',
                header: 'Discard changes',
                icon: 'pi pi-exclamation-triangle',
                acceptClass: 'p-button-danger',
                accept: () => this.$router.push('/sponsor')
            });
        },
        markDirty() {
            this.hasUnsavedChanges = true;
            if (Object.keys(this.fieldErrors).length) {
                this.validateForm();
            }
        },
        validateForm() {
            const errors = {};
            const requiredFields = [
                ['name', 'Name is required.'],
                ['description', 'Description is required.'],
                ['image', 'A banner is required.']
            ];

            requiredFields.forEach(([field, message]) => {
                if (!String(this.model[field] || '').trim()) {
                    errors[field] = message;
                }
            });

            if (!this.model.category || Number(this.model.category) === 0) {
                errors.category = 'Category is required.';
            }

            if (this.model.status === '' || this.model.status === null || this.model.status === undefined) {
                errors.status = 'Status is required.';
            }

            if (!String(this.model.url || '').trim()) {
                errors.url = 'Website is required.';
            } else if (!this.isValidWebsite(this.model.url)) {
                errors.url = 'Enter a valid website URL.';
            }

            this.fieldErrors = errors;
            return Object.keys(errors).length === 0;
        },
        isValidWebsite(url) {
            try {
                const normalizedUrl = /^https?:\/\//i.test(url.trim()) ? url.trim() : `https://${url.trim()}`;
                const parsedUrl = new URL(normalizedUrl);
                return ['http:', 'https:'].includes(parsedUrl.protocol) && Boolean(parsedUrl.hostname);
            } catch (_) {
                return false;
            }
        },
        onUploadSuccess(event){
            const response = JSON.parse(event.xhr.response);
            this.model.image = response.fileName;
            this.hasUnsavedChanges = true;
            if (this.fieldErrors.image) {
                this.validateForm();
            }

            this.$toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Banner uploaded.',
                life: 5000
            });
        },
        onUploadError(event){
            let response = {};
            try {
                response = JSON.parse(event.xhr.response);
            } catch (_) {
                response = {};
            }
            this.$toast.add({
                severity: 'error',
                summary: 'Failed to upload banner',
                detail: response.Message || 'Please choose a supported image and try again.',
                life: 5000
            });
        },
        getSponsorById(id) {
            apiService.get(`/sponsor/${id}`)
                .then(response => {
                    this.model = {
                        ...this.model,
                        ...response.data,
                        uploadFile: ''
                    };
                    this.hasUnsavedChanges = false;
                }).catch(error => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Failed to get sponsor',
                    detail: error.response?.data?.Message || 'Please try again.',
                    life: 5000
                });
            });
        },
        getCategories() {
            apiService.get('/sponsor/category')
                .then(response => {
                    this.categories = response.data || [];
                }).catch(error => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Failed to get sponsor categories',
                    detail: error.response?.data?.Message || 'Please try again.',
                    life: 5000
                });
            });
        }
    },
    mounted() {
        this.uploadUrl = `${apiService.defaults.baseURL}/file/upload`;
        this.getCategories();
        if(!this.isNew){
            this.getSponsorById(this.id);
        }
    }
})
</script>

<style scoped>
.sponsor-form {
    max-width: 1280px;
}

.form-required-note {
    color: #5f6570;
    font-size: 0.875rem;
    margin: 0 0 1rem;
}

.form-required-note span,
.required-indicator {
    color: #c62828;
    font-weight: 700;
}

.form-alert {
    align-items: flex-start;
    background: #fff1f1;
    border: 1px solid #f5c2c7;
    border-radius: 0.5rem;
    color: #9d2525;
    display: flex;
    flex-direction: column;
    font-size: 0.9rem;
    gap: 0.2rem;
    margin-bottom: 1rem;
    padding: 0.8rem 1rem;
}

.sponsor-form-section {
    background: #ffffff;
    border: 1px solid #e2e6ec;
    border-radius: 0.65rem;
    box-shadow: 0 0.15rem 0.65rem rgba(58, 59, 69, 0.05);
    height: 100%;
    padding: 1.5rem;
}

.section-heading {
    align-items: flex-start;
    border-bottom: 1px solid #edf0f4;
    display: flex;
    justify-content: space-between;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
}

.section-heading h2 {
    color: #2b2f36;
    font-size: 1.15rem;
    font-weight: 600;
    margin: 0;
}

.section-eyebrow {
    color: #426cc6;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    margin: 0 0 0.25rem;
}

.section-heading > .pi {
    color: #426cc6;
    font-size: 1.2rem;
    padding-top: 0.25rem;
}

.form-group label {
    color: #343a40;
    font-size: 0.9rem;
    font-weight: 600;
}

.form-help {
    color: #6c757d;
    display: block;
    font-size: 0.78rem;
    line-height: 1.35;
    margin-top: 0.35rem;
}

.upload-help {
    margin-bottom: 0.65rem;
}

.field-error {
    color: #c62828;
    display: block;
    font-size: 0.8rem;
    margin-top: 0.35rem;
}

.banner-preview {
    align-items: center;
    color: #5f6570;
    display: flex;
    font-size: 0.8rem;
    gap: 0.65rem;
    margin-top: 0.85rem;
}

.banner-preview img {
    border: 1px solid #e2e6ec;
    border-radius: 0.4rem;
    height: 3rem;
    object-fit: cover;
    width: 5.25rem;
}

.form-actions {
    align-items: center;
    border-top: 1px solid #e2e6ec;
    display: flex;
    justify-content: space-between;
    margin-top: 1.5rem;
    padding-top: 1.25rem;
}

.form-actions-note {
    color: #6c757d;
    font-size: 0.85rem;
    margin: 0;
}

.form-actions-buttons {
    display: flex;
}

@media (max-width: 767.98px) {
    .sponsor-form-section {
        padding: 1.15rem;
    }

    .form-actions {
        align-items: stretch;
        flex-direction: column;
        gap: 1rem;
    }

    .form-actions-buttons {
        justify-content: flex-end;
    }
}
</style>
